import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { canvasShadowCss, isThemedEmail, renderEmail } from '../email/render'

// React views of the email HTML. The builder canvas shows each section in its
// own shadow root, so the app's global CSS (which pads and colours every td)
// can't reach it and the markup on screen is the markup that gets sent.
// Previews render the whole email document in an iframe at a real width, so
// the email's own phone rules decide the layout, exactly as in an inbox.

const SHADOW_CSS = canvasShadowCss() + '.es-tag{display:inline;padding:0 4px;margin:0;border-radius:4px;background:rgba(0,126,160,0.12);color:inherit;font:inherit;font-size:0.92em;white-space:nowrap;box-decoration-break:clone;-webkit-box-decoration-break:clone}'

// Merge tags read as friendly pills in the builder ("Hi [First name],"), not
// {{FirstName}}. Only text is touched; links keep their tags, and the sent
// email is untouched.
const TAG_LABELS = { FirstName: 'First name', LastName: 'Last name', SalonName: 'Salon name', SalonAddress: 'Salon address', CompanyDetails: 'Company details' }
const TAG_RE = /\{\{\s*(\w+)\s*\}\}/g
const pillTags = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodes = []
  while (walker.nextNode()) {
    const n = walker.currentNode
    if (n.nodeValue && n.nodeValue.indexOf('{{') !== -1 && !(n.parentNode && n.parentNode.nodeName === 'STYLE')) nodes.push(n)
  }
  nodes.forEach((n) => {
    const text = n.nodeValue
    const frag = document.createDocumentFragment()
    let last = 0
    text.replace(TAG_RE, (m, key, at) => {
      if (at > last) frag.appendChild(document.createTextNode(text.slice(last, at)))
      const pill = document.createElement('span')
      pill.className = 'es-tag'
      pill.textContent = TAG_LABELS[key] || key.replace(/([a-z])([A-Z])/g, '$1 $2')
      frag.appendChild(pill)
      last = at + m.length
      return m
    })
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)))
    n.parentNode.replaceChild(frag, n)
  })
}

export const ShadowHtml = ({ html, className, style, onClick, tags = false }) => {
  const hostRef = useRef(null)
  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host) return
    if (!host.attachShadow) {
      host.innerHTML = html
      return
    }
    let root = host.shadowRoot
    if (!root) {
      root = host.attachShadow({ mode: 'open' })
      // Links carry merge tags ({{BookingLink}}) that only resolve at send
      // time. Following one here would leave the builder for a 404.
      root.addEventListener('click', (e) => {
        const path = e.composedPath ? e.composedPath() : []
        if (path.some(n => n && n.tagName === 'A')) e.preventDefault()
      })
    }
    root.innerHTML = `<style>${SHADOW_CSS}</style>${html}`
    if (tags) pillTags(root)
  }, [html, tags])
  return <div ref={hostRef} className={className} style={style} onClick={onClick} />
}

// Scale an element of a fixed native width to fit its container.
const useFitScale = (nativeWidth, maxScale = 1) => {
  const ref = useRef(null)
  const [scale, setScale] = useState(1)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const update = () => {
      const w = el.clientWidth
      if (w > 0) setScale(Math.min(maxScale, w / nativeWidth))
    }
    update()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update)
      return () => window.removeEventListener('resize', update)
    }
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [nativeWidth, maxScale])
  return [ref, scale]
}

// The whole email in an iframe at `width` (680 for desktop, 375 for a phone),
// scaled down to fit and sized to its content. Same origin, no scripts.
export const EmailFrame = ({ html, width = 600, maxScale = 1, clipHeight, className, style, title = 'Email preview', onMeasured }) => {
  const [wrapRef, scale] = useFitScale(width, maxScale)
  const frameRef = useRef(null)
  const [height, setHeight] = useState(clipHeight || 900)

  useEffect(() => {
    const frame = frameRef.current
    if (!frame) return undefined
    let ro = null
    const measure = () => {
      try {
        const doc = frame.contentDocument
        if (!doc || !doc.body) return
        // The email's own height: the document's is never less than the
        // frame, so a short email would stay as tall as the first guess
        const kids = Array.from(doc.body.children)
        const bottom = kids.reduce((m, el) => Math.max(m, el.getBoundingClientRect().bottom), 0)
        const h = Math.ceil(bottom + (doc.defaultView ? doc.defaultView.scrollY : 0)) || doc.body.scrollHeight
        if (h > 0) {
          setHeight(h)
          if (onMeasured) onMeasured(h)
        }
      } catch (e) { /* not same-origin: keep the estimate */ }
    }
    const onLoad = () => {
      measure()
      try {
        const doc = frame.contentDocument
        // Images and web fonts land after load and change the height
        doc.addEventListener('load', measure, true)
        if (typeof ResizeObserver !== 'undefined') {
          ro = new ResizeObserver(measure)
          ro.observe(doc.body)
        }
      } catch (e) { /* ignore */ }
    }
    frame.addEventListener('load', onLoad)
    return () => {
      frame.removeEventListener('load', onLoad)
      if (ro) ro.disconnect()
    }
  }, [html]) // eslint-disable-line react-hooks/exhaustive-deps

  const shownHeight = clipHeight ? Math.min(height * scale, clipHeight) : height * scale
  return (
    <div ref={wrapRef} className={className} style={{ width: '100%', ...style }}>
      <div style={{ width: Math.round(width * scale), height: Math.round(shownHeight), margin: '0 auto', overflow: 'hidden', position: 'relative' }}>
        <iframe
          ref={frameRef}
          title={title}
          srcDoc={html}
          sandbox="allow-same-origin"
          scrolling="no"
          tabIndex={-1}
          style={{ width, height, border: 0, display: 'block', transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none', background: 'transparent' }}
        />
      </div>
    </div>
  )
}

// Grid thumbnails: the iframe only mounts once the card scrolls near view,
// and photos load at half size (a picker can show 25 emails at once).
export const smallImages = (html) => html.replace(/(https:\/\/images\.unsplash\.com\/[^"?]+\?w=)(\d+)(&amp;h=|&h=)?(\d+)?/g, (m, base, w, sep, h) => (
  `${base}${Math.round(Number(w) / 2)}${sep && h ? `${sep}${Math.round(Number(h) / 2)}` : ''}`
))

// 640 wide: a desktop inbox (the email's phone rule starts below 600), with a
// little of the email's canvas either side.
export const EmailThumb = ({ html, height = 380, width = 640, className }) => {
  const ref = useRef(null)
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined')
  useEffect(() => {
    if (visible || !ref.current) return undefined
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setVisible(true); io.disconnect() }
    }, { rootMargin: '300px' })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [visible])
  return (
    <div ref={ref} className={className} style={{ height, overflow: 'hidden', position: 'relative' }}>
      {visible && <EmailFrame html={smallImages(html)} width={width} clipHeight={height} title="Template preview" />}
    </div>
  )
}

// A saved email anywhere outside the creator (campaign details, automations,
// the agent's drafts): new-style emails show the real email; classic ones
// render through `fallback`, exactly as before.
export const EmailBlocksPreview = ({ blocks, width = 680, clipHeight, fallback = null }) => {
  const salon = useSelector((state) => (state && state.user && state.user.userInfo && state.user.userInfo.hairdresser && state.user.userInfo.hairdresser.salon) || null)
  const themed = isThemedEmail(blocks)
  const name = salon && (salon.salonName || salon.name)
  const address = salon && salon.formattedAddress
  const html = useMemo(() => (
    themed ? renderEmail(blocks, { sample: { FirstName: 'Sophie', SalonName: name || '', SalonAddress: address || '' } }) : ''
  ), [blocks, themed, name, address])
  if (!themed) return typeof fallback === 'function' ? fallback() : fallback
  return clipHeight
    ? <EmailThumb html={html} width={width} height={clipHeight} />
    : <EmailFrame html={html} width={width} />
}
